import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useAskSwirlStore } from '../../stores/askSwirlStore';

/**
 * CardStack component - displays chips as stacked cards on the left
 * Similar to ChatGPT's card stack design
 * Click to expand into a horizontal row for easy deletion
 */
export default function CardStack({ isProcessing = false }) {
  // CRITICAL: Subscribe to chips array directly to ensure re-renders
  const chips = useAskSwirlStore((state) => state.chips);
  const removeChip = useAskSwirlStore((state) => state.removeChip);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const stackRef = useRef(null);

  // Debug: Log when chips change
  useEffect(() => {
    console.log('🎴 [CardStack] Chips updated:', chips.length, chips);
    // Collapse if chips are cleared while expanded
    if (chips.length === 0 && isExpanded) {
      console.log('🎴 [CardStack] Chips cleared, collapsing stack');
      setIsExpanded(false);
    }
  }, [chips.length, isExpanded]); // Depend on chips.length to force re-render

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (stackRef.current && !stackRef.current.contains(event.target) && isExpanded) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isExpanded]);

  // CRITICAL: Always log when CardStack renders to debug why chips are showing
  if (chips.length === 0) {
    return null;
  }
  
  // Log when CardStack is rendering with chips
  console.log('🎴 [CardStack] RENDERING with chips:', chips.length, 'chip IDs:', chips.map(c => c.id), 'Stack trace:', new Error().stack.split('\n').slice(1, 4).join(' -> '));

  // Sort chips by most recent first (newest has highest timestamp)
  const sortedChips = [...chips].sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0));

  // Show max 3 cards in stack (no count badge - count is only on CardSelectorButton)
  const maxVisible = 3;
  // Take the first 3 items (most recent), then reverse so newest is rendered last (on top)
  const visibleChips = sortedChips.slice(0, maxVisible).reverse();

  const handleStackClick = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="relative flex items-end pointer-events-none" ref={stackRef}>
      {isExpanded ? (
        // Expanded view: horizontal row of all cards, positioned above input bar
        <AnimatePresence>
          <motion.div
            key="expanded"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            className="absolute bottom-full mb-16 left-0 pointer-events-auto max-w-[calc(100vw-400px)] overflow-x-auto z-40"
          style={{ 
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(0,0,0,0.2) transparent'
          }}
        >
          <div className="flex items-center gap-2 pb-2 pr-4">
            {sortedChips.map((chip, index) => {
              const reverseIndex = sortedChips.length - 1 - index; // Reverse order for collapse animation
              return (
                <motion.div
                  key={chip.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ 
                    opacity: 0, 
                    x: -20, 
                    y: 10,
                    transition: { 
                      duration: 0.2, 
                      delay: reverseIndex * 0.05 
                    }
                  }}
                  transition={{ 
                    duration: 0.2, 
                    delay: index * 0.05 
                  }}
                  className="relative flex-shrink-0"
                >
                  <div 
                    onClick={(e) => {
                      // Don't collapse if clicking the X button
                      if (e.target.closest('button')) return;
                      e.stopPropagation();
                      setIsExpanded(false);
                    }}
                    className="relative w-20 h-28 rounded-lg overflow-hidden shadow-lg border border-white/20 bg-white/95 backdrop-blur-sm cursor-pointer hover:scale-105 transition-transform"
                  >
                  {/* Thumbnail/Image */}
                  {chip.thumbnail ? (
                    <img
                      src={chip.thumbnail}
                      alt={chip.label}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center">
                      <span className="text-white text-xl font-bold">
                        {chip.label.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  
                  {/* Remove button - visible on all cards when expanded */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeChip(chip.id);
                    }}
                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 hover:bg-black/90 flex items-center justify-center transition-colors z-10"
                  >
                    <X size={12} className="text-white" />
                  </button>
                  
                  {/* Label overlay */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                    <p className="text-white text-xs font-medium truncate">
                      {chip.label}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
            })}
          </div>
        </motion.div>
        </AnimatePresence>
      ) : (
        // Collapsed view: stacked cards
        <div 
          className="relative cursor-pointer pointer-events-auto"
          onClick={handleStackClick}
        >
          <AnimatePresence>
            {visibleChips.map((chip, index) => {
              const isTop = index === visibleChips.length - 1;
              const offset = index * 10; // Stack offset - increased for better visibility
              
              return (
                <motion.div
                  key={chip.id}
                  initial={{ opacity: 0, scale: 0.8, y: 20 }}
                  animate={{ 
                    opacity: 1, 
                    scale: 1, 
                    y: 0,
                    x: hoveredIndex === index ? -8 : 0,
                    zIndex: index + 1, // First card (index 0) = zIndex 1 (back), second = 2 (middle), third = 3 (front)
                  }}
                  exit={{ opacity: 0, scale: 0.8, y: 20 }}
                  transition={{ duration: 0.2 }}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  className="absolute"
                  style={{
                    bottom: `${offset}px`,
                    left: `${offset}px`,
                    transform: `translateX(${hoveredIndex === index ? -8 : 0}px)`,
                  }}
                >
                  <div className="relative w-20 h-28 rounded-lg overflow-hidden shadow-lg border border-white/20 bg-white/95 backdrop-blur-sm">
                    {/* Thumbnail/Image */}
                    {chip.thumbnail ? (
                      <img
                        src={chip.thumbnail}
                        alt={chip.label}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center">
                        <span className="text-white text-xl font-bold">
                          {chip.label.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    
                    {/* Remove button - only on top card when collapsed */}
                    {isTop && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeChip(chip.id);
                        }}
                        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 hover:bg-black/90 flex items-center justify-center transition-colors z-10"
                      >
                        <X size={12} className="text-white" />
                      </button>
                    )}
                    
                    {/* Label overlay */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                      <p className="text-white text-xs font-medium truncate">
                        {chip.label}
                      </p>
                    </div>
                    
                    {/* Pulse effect during processing */}
                    {isProcessing && isTop && (
                      <motion.div
                        animate={{
                          boxShadow: [
                            '0 0 0 0 rgba(122, 71, 245, 0)',
                            '0 0 0 4px rgba(122, 71, 245, 0.3)',
                            '0 0 0 0 rgba(122, 71, 245, 0)',
                          ],
                        }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                        }}
                        className="absolute inset-0 rounded-lg pointer-events-none"
                      />
                    )}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

